<template>
  <page-wrapper :title="title" :hasPad="true">
    <div class="wrapper">
      <service-editor
        v-model="form"
        :iconUrl="iconUrl"
        @removeDuration="handleRemoveDuration"
        @addDuration="handleAddDuration"
        @setEmptyServiceOptionCosts="handleEmptyOptionCost"
        @isCheckCombineOption="handleCheckCombineOption"
      ></service-editor>
      <div class="text-right">
        <sc-button variant="primary" @click="handleClickSaveService">บันทึก</sc-button>
      </div>
      <service-confirm-material-code
        v-if="isShowConfirmMaterialCode"
        v-model="form"
        @confirmMaterialCode="handleConfirmMaterialCode"
      ></service-confirm-material-code>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import ServiceEditor from "../components/ServiceEditor";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { DateTime } from "luxon";
import { isNil, isEmpty, range } from "ramda";
import { debounce } from "@/lib/util";
import { convertStangToBathAndRoundTwoDecimal } from "@/lib/formatter";
import ServiceConfirmMaterialCode from "../components/ServiceConfirmMaterialCode";
import { serviceType, sapServiceRecognizeRevenueType } from "@/lib/mapper";

export default {
  components: {
    PageWrapper,
    ScButton,
    ServiceEditor,
    "service-confirm-material-code": ServiceConfirmMaterialCode
  },
  data() {
    const title = "เพิ่มบริการ";
    const form = {
      name: "",
      shortDescription: "",
      minimumAdvanceDuration: 5,
      quota: 1,
      cost: 0,
      provider: null,
      externalLink: null,
      description: "",
      termsAndConditions: "",
      iconFile: null,
      serviceOptions: null,
      appointDurationSlots: [],
      appointDateConstraints: [],
      serviceOptionCosts: null,
      hasOptionSwitch: false,
      isCanNotEditService: false,
      hasEditOptionCost: true,
      materialCode: null,
      materialQuantity: null,
      sapServiceType: "NORMAL",
      codeDescriptionUnit: null,
      unitName: null,
      sapServiceRecognizeRevenue: null,
      isVatRegistered: null,
      serviceType: serviceType.SERVICE
    };

    const iconUrl = null;
    const currentQuota = form.quota;

    return {
      title,
      form,
      iconUrl,
      currentQuota,
      isCheckCombineOption: false,
      isShowConfirmMaterialCode: false
    };
  },
  watch: {
    form(form, oldForm) {
      if (this.form.iconFile === oldForm.iconFile) {
        return;
      }

      this.iconUrl = URL.createObjectURL(this.form.iconFile);
    },
    "form.quota": debounce(function (newQuota) {
      if (
        newQuota > this.currentQuota &&
        newQuota !== this.form.appointDurationSlots.length
      ) {
        this.handleAddAppointDurationSlot(newQuota, this.currentQuota);
        this.handleAddDateConstraints(newQuota, this.currentQuota);
      } else if (newQuota < this.currentQuota) {
        this.handleRemoveAppointDurationSlot(newQuota, this.currentQuota);
        this.handleRemoveDateConstraints(newQuota, this.currentQuota);
      }
      this.currentQuota = newQuota;
    }, 0.5)
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      const appointDurationSlot = {
        slotNumber: this.form.quota,
        durations: [
          {
            startAt: "",
            endAt: ""
          },
        ],
        defaultDurations: [
          {
            startAt: "09:00",
            endAt: "12:00"
          },
          {
            startAt: "13:00",
            endAt: "16:00"
          },
        ],
        isDefaultDuration: true
      };

      const appointDateConstraint = {
        slotNumber: this.form.quota,
        constraints: 0,
        reference_date_type: "PREVIOUS_APPOINTMENT_DATE",
        type: "APPOINTMENT_GAP"
      };

      this.form.appointDurationSlots.push(appointDurationSlot);
      this.form.appointDateConstraints.push(appointDateConstraint);
    },
    async handleClickSaveService() {
      if (this.form.minimumAdvanceDuration <= 0) {
        alert(
          "ไม่สามารถเพิ่มบริการได้เนื่องจากจำนวนวันนัดล่วงหน้าน้อยกว่า 1 วัน"
        );
        return;
      }

      if (this.hasEmptyDuration()) {
        alert("กรุณาใส่ช่วงเวลาให้ครบ");
        return;
      }

      if (!this.form.iconFile) {
        alert("กรุณาใส่รูป");
        return;
      }

      if (
        this.hasEmptyAppointmentCost(this.form.appointDurationSlots)
          && !this.form.serviceOptions
      ) {
        alert("กรุณากรอกราคาต้นทุนให้ครบ");
        return;
      }

      if (!this.form.provider) {
        alert("กรุณาเลือกผู้บริการ");
        return;
      }

      if (
        !this.form.cost
        && this.form.cost !== 0
        && this.form.quota === 0
        && !this.form.serviceOptions
      ) {
        alert("กรุณากรอกต้นทุนรวม");
        return;
      }

      if (this.isCheckCombineOption) {
        alert("มีการเปลี่ยน option กรุณากดสรุปราคาทุน");
        return;
      }

      if (this.form.serviceOptionCosts && this.hasEmptyCombineOption()) {
        alert("กรุณากรอกราคาต้นทุน option");
        return;
      }

      // ปิดบังคับเลือก material code (รอทาง sap ส่งข้อมูลมาให้)
      // if (
      //   !this.form.serviceOptionCosts
      //   && !this.form.materialCode
      //   && this.form.sapServiceType === "NORMAL"
      // ) {
      //   alert("กรุณากรอก Material Code");
      //   return;
      // }

      // if (
      //   !this.form.serviceOptionCosts
      //   && this.form.materialCode
      //   && !this.form.materialQuantity
      //   && this.form.sapServiceType === "NORMAL"
      // ) {
      //   alert("กรุณากรอก จำนวน / หน่วย");
      //   return;
      // }

      if (
        this.form.serviceOptions
        && this.form.hasOptionSwitch
      ) {
        alert("กรุณากดสรุปราคาทุน");
        return;
      }

      if (
        this.form.quota > 0
        && this.form.sapServiceType === "NORMAL"
        && !this.form.sapServiceRecognizeRevenue
      ) {
        alert("กรุณาเลือก รับรู้รายได้บน SAP");
        return;
      } else if (this.form.quota === 0) {
        this.form.sapServiceRecognizeRevenue = sapServiceRecognizeRevenueType.APPOINTMENT_REVENUE;
      }

      this.isShowConfirmMaterialCode = true
    },
    async handleClickSave() {
      try {
        await this.createService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        alert("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    async createService() {
      const {
        name,
        shortDescription: short_description,
        minimumAdvanceDuration: minimum_advance_duration,
        quota,
        cost,
        provider,
        externalLink,
        description,
        termsAndConditions: terms_and_conditions,
        iconFile,
        serviceOptions,
        appointDurationSlots,
        appointDateConstraints,
        serviceOptionCosts,
        materialCode,
        materialQuantity,
        sapServiceType,
        sapServiceRecognizeRevenue,
        serviceType
      } = this.form;

      const newServiceOptions = serviceOptions
        ? this.setupNewServiceOptions(serviceOptions)
        : null;

      const newServiceOptionCosts = serviceOptionCosts
        ? this.setupNewServiceOptionCosts(serviceOptionCosts)
        : null;

      const options = {
        service_options: newServiceOptions,
        service_option_costs: newServiceOptionCosts
      };

      const icon = await uploadPublicImage(iconFile);
      const appoint_duration_slots = this.setupAppointDurationSlotsForCreate(
        appointDurationSlots
      );
      const appoint_date_constraints = [];
      const provider_id = provider.id;
      const external_link = externalLink;

      const params = {
        name,
        short_description,
        minimum_advance_duration,
        quota,
        cost: convertStangToBathAndRoundTwoDecimal(cost),
        provider_id,
        external_link,
        description,
        terms_and_conditions,
        icon,
        // TODO: remove empty list when appoint date constraint and appoint duration ready on production
        appoint_duration_slots,
        appoint_date_constraints: [],
        options,
        material_code: materialCode,
        material_quantity: materialQuantity ? +materialQuantity : null,
        sap_service_type: sapServiceType,
        sap_service_recognize_revenue: sapServiceRecognizeRevenue,
        service_type: serviceType
      };

      const response = await baanRueJaiClient.admin.subscriptions.services.post(
        params
      );

      return response.data.result;
    },
    setupNewServiceOptions(serviceOptions) {
      const newServiceOptions = serviceOptions.map((option) => {
        const variants = option.variants.map((variant) => {
          return {
            variant_id: variant.variant_id,
            is_not_sale: variant.is_not_sale,
            is_out_of_stock: variant.is_out_of_stock,
            title: variant.title,
            material_code: variant.material_code,
            material_quantity: variant.material_quantity
          };
        });

        return {
          id: option.id,
          extra_option: option.extra_option,
          title: option.title,
          variants
        };
      });

      return newServiceOptions;
    },
    setupNewServiceOptionCosts(serviceOptionCosts) {
      const newServiceOptionCosts = serviceOptionCosts.map((optionCost) => {
        const combinationCosts = optionCost.combination_costs.map((combinationCost) => {
          const appointmentCosts = combinationCost.appointment_costs.map((appointmentCosts) => {

            return {
              appoint_no: appointmentCosts.appoint_no,
              cost: appointmentCosts.cost !== null
                ? parseFloat(appointmentCosts.cost)
                : null
            }
          })

          return {
            id: combinationCost.id,
            is_not_sale: combinationCost.is_not_sale,
            is_out_of_stock: combinationCost.is_out_of_stock,
            title: combinationCost.title,
            appointment_costs: appointmentCosts,
            material_code: combinationCost.material_code,
            material_quantity: combinationCost.material_quantity
          }
        })

        return {
          id: optionCost.id,
          is_product: optionCost.is_product,
          title: optionCost.title,
          combination_costs: combinationCosts
        }
      })

      return newServiceOptionCosts;
    },
    setupAppointDurationSlotsForCreate(appointDurationSlots) {
      const newAppointDurationSlots = appointDurationSlots.map((slot) => {
        const slot_number = slot.slotNumber.toString();
        const durations = slot.isDefaultDuration
          ? this.setupDurationsForCreate(slot.defaultDurations)
          : this.setupDurationsForCreate(slot.durations);
        const appointCost = convertStangToBathAndRoundTwoDecimal(slot.appointCost);

        return {
          slot_number,
          durations,
          appointCost
        };
      });

      return newAppointDurationSlots;
    },
    setupDurationsForCreate(durations) {
      const formattedDurations = durations.map(({ startAt, endAt }) => {
        const start_at = DateTime.fromString(startAt, "HH:mm").toFormat(
          "HH:mm:ssZZ"
        );
        const end_at = DateTime.fromString(endAt, "HH:mm").toFormat(
          "HH:mm:ssZZ"
        );

        return { start_at, end_at };
      });

      return formattedDurations;
    },
    hasEmptyDuration() {
      const hasEmptyDuration = this.form.appointDurationSlots.some((slot) =>
        !slot.isDefaultDuration ? this.isEmptyDurations(slot.durations) : false
      );

      return hasEmptyDuration;
    },
    isEmptyDurations(durations) {
      return durations.some(
        (duration) =>
          isEmpty(duration.startAt) ||
          isEmpty(duration.endAt) ||
          isNil(duration.startAt) ||
          isNil(duration.endAt)
      );
    },
    handleRemoveDuration(dataForDelete) {
      const { slotNumber, durationIndex } = dataForDelete;

      this.form.appointDurationSlots
        .find((slot) => slot.slotNumber === slotNumber)
        .durations.splice(durationIndex, 1);
    },
    handleAddDuration(slotNumber) {
      this.form.appointDurationSlots
        .find((slot) => slot.slotNumber === slotNumber)
        .durations.push({ startAt: "", endAt: "" });
    },
    handleAddAppointDurationSlot(newQuota, oldQuota) {
      const quotaNumbers = range(oldQuota + 1, newQuota + 1);
      const newAppointDurationSlots = quotaNumbers.map((quota) => {
        const newAppointDurationSlot = {
          slotNumber: quota,
          durations: [
            {
              startAt: "",
              endAt: ""
            },
          ],
          defaultDurations: [
            {
              startAt: "09:00",
              endAt: "12:00"
            },
            {
              startAt: "13:00",
              endAt: "16:00"
            },
          ],
          isDefaultDuration: true
        };

        return newAppointDurationSlot;
      });

      this.form.appointDurationSlots = this.form.appointDurationSlots.concat(
        newAppointDurationSlots
      );
    },
    handleRemoveAppointDurationSlot(newQuota, oldQuota) {
      this.form.appointDurationSlots.splice(newQuota, oldQuota - newQuota);
    },
    handleAddDateConstraints(newQuota, oldQuota) {
      const quotaNumbers = range(oldQuota + 1, newQuota + 1);
      const newAppointDateConstraints = quotaNumbers.map((quota) => {
        const newAppointDateConstraint = {
          slotNumber: quota,
          constraints: 0,
          reference_date_type: "PREVIOUS_APPOINTMENT_DATE",
          type: "APPOINTMENT_GAP"
        };

        return newAppointDateConstraint;
      });

      this.form.appointDateConstraints = this.form.appointDateConstraints.concat(
        newAppointDateConstraints
      );
    },
    handleRemoveDateConstraints(newQuota, oldQuota) {
      this.form.appointDateConstraints.splice(newQuota, oldQuota - newQuota);
    },
    hasEmptyAppointmentCost(appointmentCost) {
      const hasEmptyAppointCost = appointmentCost.some((slot) =>
        !slot.appointCost ? true : false
      );

      return hasEmptyAppointCost;
    },
    hasEmptyCombineOption() {
      let hasOptionCost;
        if (this.form.serviceOptionCosts) {
        hasOptionCost = this.form.serviceOptionCosts.some(
          (appointOption) => {
            const options = appointOption.combination_costs.some(
              (combineOption) => {
                return combineOption.appointment_costs.some(
                  (item) => item.cost === null ? true : false
                );
              }
            );

            return options;
          }
        );
      }

      return hasOptionCost;
    },
    handleEmptyOptionCost() {
      this.form.serviceOptionCosts = null;
    },
    handleCheckCombineOption(value) {
      this.isCheckCombineOption = value;
    },
    handleConfirmMaterialCode(value) {
      if (value) {
        this.handleClickSave();
      }

      this.isShowConfirmMaterialCode = false
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/services/pages/ServiceCreatePage.vue